import { useRoute, useRuntimeConfig } from '#imports'
import { ApiValidationError } from '~~/errors/ApiValidationError'
import { ConcurrentChangeError, type ConcurrentField, type OrganizationKey } from '~~/errors/ConcurrentChangeError'
import { EAuthorizationError } from '~~/errors/EAuthorizationError'
import { ENoticeRequiredError } from '~~/errors/ENoticeRequiredError'
import { EMaintenanceWriteBlockedError } from '~~/errors/EMaintenanceWriteBlockedError'
import { setPendingLogoutToast, storeAuthRedirectTarget } from '~/utils/authRedirect'
import { useDeploymentMaintenance } from '~/composables/api/useDeploymentMaintenance'
import { useSourceToast } from '~/composables/ui/useSourceToast'
import {
  formatDevProfilesApiHeader,
  readDevGardianOverrideState,
} from '~~/utils/devOidcProfileOverride'

export type THttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export type ApiMonOrgaParams = Record<string, string | number | boolean | undefined>

type ApiValidationErrorItem = { field: string; message: string }

interface ApiErrorBody {
  message?: string
  status?: number
  error?: unknown
  errors?: ApiValidationErrorItem[]
  code?: string
  fields?: ConcurrentField[]
  currentKey?: OrganizationKey | null
}

interface FetchLikeError {
  status?: number
  statusCode?: number
  data?: ApiErrorBody
  response?: { status?: number; _data?: ApiErrorBody }
}

const FALLBACK_MESSAGES: Record<number, string> = {
  400: 'Données invalides. Vérifiez les champs du formulaire.',
  404: 'Élément introuvable.',
  409: 'Un conflit a été détecté : ce nom court ou cette combinaison existe déjà pour une autre direction.',
  422: 'Les données envoyées sont incorrectes. Vérifiez le formulaire.',
}

function getStatus(error: FetchLikeError): number | undefined {
  return error?.status ?? error?.statusCode ?? error?.response?.status
}

function getErrorBody(error: FetchLikeError): ApiErrorBody | undefined {
  return error?.data ?? error?.response?._data
}

/** Attente entre deux tentatives GET (API indisponible / redémarrage conteneur). */
function delayForRetryMs(ms: number): Promise<void> {
  return new Promise(function scheduleRetryResolve(resolve) {
    setTimeout(resolve, ms)
  })
}

/**
 * Erreurs typiques quand le back est coupé ou redémarre (proxy Nitro, connexion refusée).
 * On ne retente pas les statuts métier (401, 403, 404, …).
 */
function isTransientGetFailure(error: unknown): boolean {
  const status = getStatus(error as FetchLikeError)
  if (status === undefined) {
    return true
  }
  return (status === 502 || status === 503 || status === 504 || status === 408);

}

type ErrorWithHttpStatus = Error & FetchLikeError

function attachHttpStatusToError(message: string, status: number | undefined): ErrorWithHttpStatus {
  const err = new Error(message) as ErrorWithHttpStatus
  if (status !== undefined) {
    err.status = status
    err.statusCode = status
  }
  return err
}

function throwForStatus(
  status: number | undefined,
  body: ApiErrorBody | undefined,
  original: unknown
): never {
  const apiMessage = body?.message
  if (typeof apiMessage === 'string' && apiMessage.length > 0) {
    throw attachHttpStatusToError(apiMessage, status)
  }
  const fallback =
    status !== undefined ? FALLBACK_MESSAGES[status] : undefined
  if (typeof fallback === 'string') {
    throw attachHttpStatusToError(fallback, status)
  }
  throw original
}

type ApiSession = {
  route: ReturnType<typeof useRoute>
  user: { value?: { accessToken?: string } | null }
  logout: () => Promise<unknown>
  loggedIn: { value: boolean }
}

type MaintenanceGate = {
  shouldBlockRequest: (method: string) => boolean
  mutationBlockedMessage: () => string
  consumeGraceWrite: () => void
}

type ApiToast = {
  info: (title: string, description: string) => void
}

type ApiFetchRequest = ApiSession & {
  method: THttpMethod
  endpoint: string
  params?: ApiMonOrgaParams
  body?: unknown
  accessToken: string
  baseURL: string
  maintenance: MaintenanceGate
  toast: ApiToast
}

export async function useApiMonOrga<T = unknown>(
  method: THttpMethod,
  endpoint: string,
  params?: ApiMonOrgaParams,
  body?: unknown
): Promise<T> {
  const config = useRuntimeConfig()
  const route = useRoute()
  const { user, logout, loggedIn } = useOidcAuth()
  const maintenance = useDeploymentMaintenance({ autoRefresh: false })
  const toast = useSourceToast()
  const session: ApiSession = { route, user, logout, loggedIn }

  const accessToken = user.value?.accessToken
  if (!accessToken) {
    return handleMissingAccessToken<T>(session)
  }

  assertMutationAllowed(maintenance, method, endpoint)

  return fetchMonOrgaWithRetry<T>({
    ...session,
    method,
    endpoint,
    params,
    body,
    accessToken,
    baseURL: config.public.apiBaseUrl as string,
    maintenance,
    toast,
  })
}

/**
 * Playwright visual snapshots (public-snapshots) run without auth.
 * Never trigger an auth navigation in that mode, otherwise the browser is redirected to WebSSO
 * and snapshot assertions time out.
 */
async function handleMissingAccessToken<T>(session: ApiSession): Promise<T> {
  if (isPlaywrightSnapshot(session.route)) {
    throw new EAuthorizationError('Authentification désactivée (mode snapshots Playwright).')
  }

  storeAuthRedirectTarget(session.route.fullPath)
  if (session.loggedIn.value) {
    setPendingLogoutToast()
    await session.logout()
    throw new EAuthorizationError('Session expirée. Merci de vous reconnecter.')
  }
  return (await navigateTo({
    path: '/login',
    query: { monorga_return_to: session.route.fullPath },
  })) as T
}

function isPlaywrightSnapshot(route: ReturnType<typeof useRoute>): boolean {
  return String(route.query.pw ?? '') === '1'
}

function assertMutationAllowed(
  maintenance: MaintenanceGate,
  method: THttpMethod,
  endpoint: string
): void {
  if (endpoint.startsWith('/v1/maintenance/')) {
    return
  }
  if (maintenance.shouldBlockRequest(method)) {
    throw new EMaintenanceWriteBlockedError(maintenance.mutationBlockedMessage())
  }
}

async function fetchMonOrgaWithRetry<T>(request: ApiFetchRequest): Promise<T> {
  const maxFetchAttempts = request.method === 'GET' ? 3 : 1
  const devProfilesHeader = readDevProfilesHeader()
  let lastFetchError: unknown

  for (let attemptIndex = 0; attemptIndex < maxFetchAttempts; attemptIndex++) {
    try {
      return await executeApiFetch<T>(request, devProfilesHeader)
    } catch (error: unknown) {
      lastFetchError = error
      if (!shouldRetryGet(request.method, attemptIndex, maxFetchAttempts, error)) {
        break
      }
      await delayForRetryMs(500 * (attemptIndex + 1))
    }
  }

  return mapApiFailure<T>(request, lastFetchError)
}

function readDevProfilesHeader(): string | undefined {
  if (!import.meta.dev) {
    return undefined
  }
  const devOverride = readDevGardianOverrideState()
  if (!devOverride.replaceActive) {
    return undefined
  }
  return formatDevProfilesApiHeader(devOverride.profiles)
}

function shouldRetryGet(
  method: THttpMethod,
  attemptIndex: number,
  maxFetchAttempts: number,
  error: unknown
): boolean {
  if (method !== 'GET') {
    return false
  }
  if (attemptIndex >= maxFetchAttempts - 1) {
    return false
  }
  return isTransientGetFailure(error)
}

async function executeApiFetch<T>(
  request: ApiFetchRequest,
  devProfilesHeader: string | undefined
): Promise<T> {
  const fetchOptions = buildFetchOptions(request, devProfilesHeader)
  if (request.method === 'GET') {
    const payload = await $fetch<T>(request.endpoint, fetchOptions)
    return payload as T
  }

  const result = await $fetch.raw<T>(request.endpoint, fetchOptions)
  request.maintenance.consumeGraceWrite()
  return readMutationPayload(result.headers, result._data as T, request.toast)
}

function buildFetchOptions(
  request: ApiFetchRequest,
  devProfilesHeader: string | undefined
) {
  return {
    baseURL: request.baseURL,
    method: request.method,
    ...queryParamsOption(request.params),
    headers: {
      Authorization: `Bearer ${request.accessToken}`,
      Accept: 'application/json; charset=utf-8',
      ...devProfilesHeaderOption(devProfilesHeader),
    },
    ...bodyOption(request.body),
  }
}

function queryParamsOption(params?: ApiMonOrgaParams): { params?: ApiMonOrgaParams } {
  if (params == null || Object.keys(params).length === 0) {
    return {}
  }
  return { params }
}

function devProfilesHeaderOption(
  devProfilesHeader: string | undefined
): Record<string, string> {
  if (devProfilesHeader === undefined) {
    return {}
  }
  return { 'X-MonOrga-Dev-Profiles': devProfilesHeader }
}

function bodyOption(body: unknown): { body?: BodyInit | Record<string, unknown> } {
  if (body == null) {
    return {}
  }
  return { body: body as BodyInit | Record<string, unknown> }
}

function readMutationPayload<T>(
  headers: { get(name: string): string | null } | undefined,
  data: T,
  toast: ApiToast
): T {
  const restoredHeader = headers?.get('X-MonOrga-Restored')
  if (import.meta.client && restoredHeader === '1') {
    toast.info(
      'Restauration effectuée',
      'Une entité existait déjà dans la corbeille avec le même identifiant : elle a été restaurée.'
    )
  }
  return data
}

async function mapApiFailure<T>(request: ApiFetchRequest, error: unknown): Promise<T> {
  if (error instanceof EMaintenanceWriteBlockedError) {
    throw error
  }
  const err = error as FetchLikeError
  const status = getStatus(err)
  const errorBody = getErrorBody(err)

  if (status === 401) {
    await rejectExpiredSession(request)
  }
  if (status === 403) {
    throwForbiddenOrNotice(errorBody)
  }
  if (status === 422) {
    throwValidationError(errorBody)
  }
  if (status === 409) {
    throwConcurrentChange(errorBody)
  }
  throwForStatus(status, errorBody, error)
}

function throwConcurrentChange(errorBody: ApiErrorBody | undefined): void {
  if (errorBody?.code !== 'concurrentChange' && errorBody?.code !== 'deleted') {
    return
  }
  throw new ConcurrentChangeError(
    concurrentMessage(errorBody),
    errorBody.code,
    Array.isArray(errorBody.fields) ? errorBody.fields : [],
    errorBody.currentKey ?? null
  )
}

function concurrentMessage(errorBody: ApiErrorBody): string {
  if (typeof errorBody.message === 'string' && errorBody.message.length > 0) {
    return errorBody.message
  }
  return FALLBACK_MESSAGES[409] ?? 'Conflit de modification.'
}

async function rejectExpiredSession(session: ApiSession): Promise<never> {
  if (!isPlaywrightPersonaToken(session.user.value?.accessToken)) {
    storeAuthRedirectTarget(session.route.fullPath)
    setPendingLogoutToast()
    await session.logout()
  }
  throw new EAuthorizationError('Session expirée. Merci de vous reconnecter.')
}

function isPlaywrightPersonaToken(accessToken: string | undefined): boolean {
  return typeof accessToken === 'string' && accessToken.startsWith('playwright-persona:')
}

function throwForbiddenOrNotice(errorBody: ApiErrorBody | undefined): never {
  if (errorBody?.code === 'NOTICE_REQUIRED') {
    throw new ENoticeRequiredError(noticeRequiredMessage(errorBody))
  }
  throw new EAuthorizationError(forbiddenMessage(errorBody))
}

function noticeRequiredMessage(errorBody: ApiErrorBody | undefined): string {
  if (typeof errorBody?.message === 'string' && errorBody.message.length > 0) {
    return errorBody.message
  }
  return 'Vous devez accepter la notice pour poursuivre.'
}

function forbiddenMessage(errorBody: ApiErrorBody | undefined): string {
  if (typeof errorBody?.message === 'string' && errorBody.message.trim().length > 0) {
    return errorBody.message.trim()
  }
  return 'Accès refusé (droits insuffisants).'
}

function throwValidationError(errorBody: ApiErrorBody | undefined): void {
  if (!Array.isArray(errorBody?.errors)) {
    return
  }
  throw new ApiValidationError(validationMessage(errorBody), groupFieldErrors(errorBody.errors))
}

function validationMessage(errorBody: ApiErrorBody | undefined): string {
  if (typeof errorBody?.message === 'string' && errorBody.message.length > 0) {
    return errorBody.message
  }
  return FALLBACK_MESSAGES[422] ?? 'Erreur inconnue.'
}

function groupFieldErrors(errors: ApiValidationErrorItem[]): Record<string, string[]> {
  const fieldErrors: Record<string, string[]> = {}
  for (const { field, message } of errors) {
    const messages = fieldErrors[field] ?? []
    messages.push(message)
    fieldErrors[field] = messages
  }
  return fieldErrors
}
